import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Check, PriorityHigh } from '@material-ui/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareSquare } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import {
  ICompany,
  ProjectArchiveStatus,
  ProjectArchiveSummary,
} from '../../api-client/autogenerated';
import { openFileInNewTab, parseDate } from '../../scripts/utils';
import { getFileUrlById } from '../../models/api/files';
import { getProjectArchiveShareRecipients, shareProjectArchiveZip } from '../../models/api/project';
import { describeArchiveSelection } from '../custom-components/document-type-utils';
import DownloadIcon from '../icons/Download-icon';
import EmailUsersDialog from '../design/EmailUsersDialog';
import ExportContentsDialog from './ExportContentsDialog';

export const EXPORTS_PER_PAGE = 5;

type ProjectArchivesListProps = {
  archives: ProjectArchiveSummary[];
  isLoading: boolean;
  projectId: string;
  projectName: string;
  /** How many document types the picker offers, so a full selection can be summarised as one word. */
  totalSelectableDocumentTypes: number;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    tableContainer: {
      // The status and the actions must never be the ones that get clipped.
      overflowX: 'auto',
      border: '1px solid #949494',
      borderRadius: 4,
    },
    cell: {
      whiteSpace: 'nowrap',
      paddingTop: 4,
      paddingBottom: 4,
    },
    selectionCell: {
      maxWidth: 190,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingTop: 4,
      paddingBottom: 4,
    },
    selectionButton: {
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    iconButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      marginLeft: 12,
    },
    ready: {
      color: theme.palette.primary.main,
      fontWeight: 500,
    },
    unavailable: {
      color: '#616061',
    },
    failed: {
      color: '#ED3F26',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      color: '#2C69D6',
      marginTop: 8,
    },
    currencyCell: {
      width: 42,
      paddingTop: 4,
      paddingBottom: 4,
      paddingRight: 0,
    },
    currencyCheckbox: {
      padding: 0,
      // The box carries its own colour, so the disabled grey must not wash it out.
      '&.Mui-disabled': {
        color: 'inherit',
      },
    },
    currencyBox: {
      width: 18,
      height: 18,
      borderRadius: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#FFFFFF',
    },
    currentBox: {
      backgroundColor: '#2BB073',
    },
    supersededBox: {
      backgroundColor: '#ED3F26',
    },
    currencyGlyph: {
      fontSize: 16,
    },
  }),
);

/** Only a finished export with a file behind it can be downloaded or shared. */
const isReady = (archive: ProjectArchiveSummary) =>
  archive.status === ProjectArchiveStatus.Complete && !!archive.fileId;

const getStatusLabel = (archive: ProjectArchiveSummary) => {
  switch (archive.status) {
    case ProjectArchiveStatus.Complete:
      return archive.fileId ? 'Ready' : 'File unavailable';
    case ProjectArchiveStatus.InProgress:
      return archive.percentComplete !== undefined
        ? `In progress — ${archive.percentComplete}%`
        : 'In progress';
    case ProjectArchiveStatus.Stalled:
      return 'Did not finish';
    case ProjectArchiveStatus.Failed:
    default:
      return 'Failed';
  }
};

/**
 * The project's exports. An export outlives the dialog that started it, so this is how someone
 * picks a finished one back up rather than waiting on the page for it.
 */
export default function ProjectArchivesList(props: ProjectArchivesListProps) {
  const { archives, isLoading, projectId, projectName, totalSelectableDocumentTypes } = props;
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [contentsArchive, setContentsArchive] = useState<ProjectArchiveSummary>();
  const [shareArchive, setShareArchive] = useState<ProjectArchiveSummary>();
  const [shareCompanies, setShareCompanies] = useState<ICompany[]>();

  const pageCount = Math.ceil(archives.length / EXPORTS_PER_PAGE);

  // Exports finishing while the dialog is open can shrink the list out from under the page.
  useEffect(() => {
    if (page > 0 && page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  const visibleArchives = useMemo(
    () => archives.slice(page * EXPORTS_PER_PAGE, page * EXPORTS_PER_PAGE + EXPORTS_PER_PAGE),
    [archives, page],
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
        <CircularProgress size={24} />
      </div>
    );
  }

  if (archives.length === 0) {
    return (
      <Typography className={classes.unavailable} style={{ paddingBottom: 8 }}>
        This project has not been exported yet.
      </Typography>
    );
  }

  return (
    <>
      <div className={classes.tableContainer}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.currencyCell} />
              <TableCell className={classes.cell}>Requested</TableCell>
              <TableCell className={classes.cell}>By</TableCell>
              <TableCell className={classes.cell}>Includes</TableCell>
              <TableCell className={classes.cell}>Status</TableCell>
              <TableCell className={classes.cell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleArchives.map((archive) => {
              const selectionSummary = describeArchiveSelection(
                archive,
                totalSelectableDocumentTypes,
              );
              return (
                <TableRow key={archive.jobStatusId}>
                  <TableCell className={classes.currencyCell}>
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        archive.isCurrent
                          ? 'Current export'
                          : 'Not current. Activity detected after this export.'
                      }
                    >
                      {/* Tooltips need an enabled element to hang off, so the box wraps it. */}
                      <span>
                        <Checkbox
                          disabled
                          checked
                          disableRipple
                          className={classes.currencyCheckbox}
                          aria-label={archive.isCurrent ? 'Current export' : 'Not current'}
                          checkedIcon={
                            <span
                              className={clsx(
                                classes.currencyBox,
                                archive.isCurrent ? classes.currentBox : classes.supersededBox,
                              )}
                            >
                              {archive.isCurrent ? (
                                <Check className={classes.currencyGlyph} />
                              ) : (
                                <PriorityHigh className={classes.currencyGlyph} />
                              )}
                            </span>
                          }
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {archive.requestedOn
                      ? parseDate(archive.requestedOn).format('MM/DD/YYYY h:mm A')
                      : '—'}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {archive.requestedByUserName ?? '—'}
                  </TableCell>
                  <TableCell className={classes.selectionCell}>
                    <Tooltip arrow title="See everything this export contains" placement="top">
                      <button
                        type="button"
                        className={classes.selectionButton}
                        onClick={() => setContentsArchive(archive)}
                      >
                        {selectionSummary}
                      </button>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Tooltip arrow title={archive.error ?? ''} placement="top">
                      <span
                        className={
                          isReady(archive)
                            ? classes.ready
                            : archive.status === ProjectArchiveStatus.Failed
                            ? classes.failed
                            : classes.unavailable
                        }
                      >
                        {getStatusLabel(archive)}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell className={classes.cell} align="right">
                    {/* Nothing to download or share until the export is actually ready. */}
                    {isReady(archive) && (
                      <>
                        <Tooltip arrow title="Download this export" placement="top">
                          <button
                            type="button"
                            aria-label="Download this export"
                            className={classes.iconButton}
                            onClick={() => openFileInNewTab(archive.fileId!)}
                          >
                            <DownloadIcon />
                          </button>
                        </Tooltip>
                        <Tooltip arrow title="Get a link to this export" placement="top">
                          <button
                            type="button"
                            aria-label="Get a link to this export"
                            className={classes.iconButton}
                            onClick={() => {
                              // The project's users cannot come from the loaded project here: an
                              // export only exists on an archived project, and those never load.
                              getProjectArchiveShareRecipients(projectId)
                                .then(setShareCompanies)
                                .catch(() => setShareCompanies([]));
                              setShareArchive(archive);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faShareSquare}
                              style={{ width: 18, height: 18, color: '#7A797A' }}
                            />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {pageCount > 1 && (
        <Pagination
          count={pageCount}
          shape="rounded"
          color="primary"
          size="small"
          page={page + 1}
          onChange={(_event, newPage) => setPage(newPage - 1)}
          className={classes.pagination}
        />
      )}

      <ExportContentsDialog
        archive={contentsArchive}
        closeDialog={() => setContentsArchive(undefined)}
      />
      <EmailUsersDialog
        dialogOpen={!!shareArchive}
        closeDialog={() => setShareArchive(undefined)}
        selectableCompanies={shareCompanies}
        projectName={projectName}
        onSubmit={async (emails, note) => {
          if (!shareArchive?.fileId || emails.length === 0) return;
          await shareProjectArchiveZip(projectId, shareArchive.fileId, emails, note);
        }}
        getLink={async () => {
          if (!shareArchive?.fileId) return '';
          const { downloadLink } = await getFileUrlById(shareArchive.fileId, true);
          return downloadLink ?? '';
        }}
        defaultSubject={`Export of ${projectName}`}
      />
    </>
  );
}
